import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

interface AuthProps {
  onClose: () => void;
}

const Auth: React.FC<AuthProps> = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, signup } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password, firstName, lastName);
        setError('');
        setMessage('Account created. Check your email for a verification link before using the app.');
        setLoading(false);
        return;
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.92)' }}>
      <div style={{
        backgroundColor: '#000000',
        border: '2px solid #ffffff',
        borderRadius: 0,
        maxWidth: '28rem',
        width: '100%',
        padding: '2rem'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            color: '#ffffff',
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}>
            {isLogin ? 'Login' : 'Sign Up'}
          </h2>
          <p style={{
            color: '#ffffff',
            fontSize: '0.875rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            {isLogin ? 'Welcome back to PromptX' : 'Create your PromptX account'}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: 'rgba(231, 76, 60, 0.1)',
            border: '1px solid #e74c3c',
            borderRadius: 0,
            padding: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{
              color: '#e74c3c',
              fontSize: '0.875rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              {error}
            </p>
          </div>
        )}

        {message && (
          <div style={{
            backgroundColor: 'rgba(46, 204, 113, 0.1)',
            border: '1px solid #2ecc71',
            borderRadius: 0,
            padding: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{
              color: '#2ecc71',
              fontSize: '0.875rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              {message}
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ marginBottom: '1.5rem' }}>
          {!isLogin && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  marginBottom: '0.5rem',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.05em'
                }}>
                  FIRST NAME
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required={!isLogin}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(245, 245, 220, 0.05)',
                    border: '1px solid rgba(245, 245, 220, 0.2)',
                    borderRadius: 0,
                    color: '#ffffff',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(245, 245, 220, 0.2)'}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#ffffff',
                  marginBottom: '0.5rem',
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: '0.05em'
                }}>
                  LAST NAME
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required={!isLogin}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'rgba(245, 245, 220, 0.05)',
                    border: '1px solid rgba(245, 245, 220, 0.2)',
                    borderRadius: 0,
                    color: '#ffffff',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(245, 245, 220, 0.2)'}
                />
              </div>
            </div>
          )}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              EMAIL
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'rgba(245, 245, 220, 0.05)',
                border: '1px solid rgba(245, 245, 220, 0.2)',
                borderRadius: 0,
                color: '#ffffff',
                fontFamily: "'Inter', sans-serif",
                fontSize: '0.9rem',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ffffff'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(245, 245, 220, 0.2)'}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              PASSWORD
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  paddingRight: '3rem',
                  backgroundColor: 'rgba(245, 245, 220, 0.05)',
                  border: '1px solid rgba(245, 245, 220, 0.2)',
                  borderRadius: 0,
                  color: '#ffffff',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '0.9rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(245, 245, 220, 0.2)'}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  padding: '0.35rem 0.6rem',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  borderRadius: 0,
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.color = '#000000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#ffffff';
                }}
              >
                {showPassword ? 'Hide' : 'View'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? 'rgba(245, 245, 220, 0.5)' : '#ffffff',
              border: '1px solid #ffffff',
              color: '#000000',
              padding: '0.875rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0,
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Processing...' : (isLogin ? 'Login' : 'Sign Up')}
          </button>
        </form>

        {/* Toggle Login/Signup */}
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <p style={{
            color: '#ffffff',
            fontSize: '0.875rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            {isLogin ? "Don't have an account?" : "Already have an account?"}
            {' '}
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setMessage('');
                setShowPassword(false);
              }}
              style={{
                color: '#ffffff',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                textDecoration: 'underline',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.02em'
              }}
            >
              {isLogin ? 'Sign Up' : 'Login'}
            </button>
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            background: 'transparent',
            border: '1px solid #ffffff',
            color: '#ffffff',
            padding: '0.75rem',
            fontSize: '0.85rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontFamily: "'Inter', sans-serif",
            borderRadius: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ffffff';
            e.currentTarget.style.color = '#000000';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#ffffff';
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default Auth;



